/** What the tenant-wide `messages` subscription says about one message (ids and flags only). */
export interface ThreadEvent {
  conversationId?: string | null;
  isNote?: boolean | null;
  isForward?: boolean | null;
  isInternal?: boolean | null;
}

/** A message of the loaded ticket, as far as the live announcement reads it. */
export interface LiveMessage {
  id: string;
  isNote: boolean;
  isForward?: boolean | null;
  isInternal?: boolean | null;
  parentMessageId?: string | null;
  agent?: { id: string } | null;
  emailFromName?: string | null;
  emailFromAddress?: string | null;
}

/**
 * Whether an event of the tenant-wide subscription changes the thread of the open ticket: a
 * note, a send to colleagues or an internal row (a colleague's mail reply, #717) of THIS
 * conversation. Every other event, above all another ticket's, must not refetch (T-02-27,
 * T-03-39).
 */
export function affectsTicketThread(
  event: ThreadEvent | null | undefined,
  ticketId: string,
): boolean {
  if (event?.conversationId !== ticketId) return false;
  return Boolean(event.isNote || event.isForward || event.isInternal);
}

/** A message that lives in a note thread or a send thread: a note, a send or an internal row. */
export const isThreadMessage = (message: LiveMessage): boolean =>
  Boolean(message.isNote || message.isForward || message.isInternal);

/**
 * The thread messages of `after` that `known` has not seen, remembered in `known` so a second
 * event cannot announce the same message twice.
 */
export function takeFreshThreadMessages<T extends LiveMessage>(
  after: readonly T[],
  known: Set<string>,
): T[] {
  const fresh = after.filter(
    (message) => isThreadMessage(message) && !known.has(message.id),
  );
  fresh.forEach((message) => known.add(message.id));
  return fresh;
}

export interface ReplyAnnouncement {
  /** The `tickets.notes.status.*` key to announce. */
  key: "newNote" | "newReply";
  /** Who wrote it; null when nothing names the writer (the caller says "Unbekannter Nutzer"). */
  name: string | null;
}

/**
 * What a screen reader hears when messages arrive: the latest fresh note or reply that someone
 * else wrote. A colleague's mail reply is named by its display name, else its address; a send is
 * the sender's own act and is not announced. Null when there is nothing to say.
 */
export function announcementFor<T extends LiveMessage>(
  fresh: readonly T[],
  viewerId: string,
  nameOf: (message: T) => string | null,
): ReplyAnnouncement | null {
  // An internal row that answers nothing (a connector's note, a staff mail) is not announced.
  const latest = fresh.findLast(
    (message) =>
      !message.isForward &&
      message.agent?.id !== viewerId &&
      (message.isNote || Boolean(message.parentMessageId)),
  );
  if (!latest) return null;
  const mailReply = !latest.isNote;
  const name = mailReply
    ? latest.emailFromName?.trim() || latest.emailFromAddress?.trim() || null
    : nameOf(latest);
  return {
    key: latest.parentMessageId ? "newReply" : "newNote",
    name,
  };
}
